import ReportSectionCard from './shared/ReportSectionCard.component'
import ReportSectionHeading from '@components/pages/Reports/shared/ReportSectionHeading.component'
import React, { useMemo, useState } from 'react'
import ReportViewTabs from '@components/pages/Reports/shared/ReportViewTabs.component'
import ReportDataTable, {
  ReportDataTableColumn,
} from '@components/pages/Reports/shared/ReportDataTable.component'
import ReportFullViewTableTitle, {
  useReportFullViewSection,
} from '@components/pages/Reports/shared/ReportFullViewTableTitle.component'
import ReportExpandableSection from '@components/pages/Reports/shared/ReportExpandableSection.component'
import ReportBundleStickyTopWhenExpanded from '@components/pages/Reports/shared/ReportBundleStickyTopWhenExpanded.component'
import ReportFilterToolbar from '@components/pages/Reports/shared/ReportFilterToolbar.component'
import ReportKpiGrid from '@components/pages/Reports/shared/ReportKpiGrid.component'
import ReportPageHeader from '@components/pages/Reports/shared/ReportPageHeader.component'
import { REPORTS_DISTRIBUTION_VIEW_TABS } from '@constants/Reports/patientBaseHealth.constants'
import {
  FEEDBACK_CHANNEL_ROWS,
  FeedbackChannelRow,
  NPS_FEEDBACK_TREND_MONTHLY,
  REFERRAL_MONTHLY_MADE_DATA,
  REFERRAL_REPUTATION_FILTERS,
  REFERRAL_REPUTATION_KPI_METRICS,
  REFERRAL_VALUE_BUCKET_ROWS,
} from '@constants/Reports/referralReputation.constants'
import { ReportsDistributionView } from '@dataTypes/reports.type'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

const REPORT_CHART_AXIS_TICK = { fontSize: 12, fill: 'var(--gray-500)' }
const REPORT_CHART_TOOLTIP_STYLE = {
  borderRadius: 8,
  border: '1px solid var(--gray-200)',
  fontSize: 12,
}
const REPORT_CHART_GRID_STROKE = 'var(--gray-100)'
const REPORT_LINE_CHART_MARGIN = { top: 8, right: 16, left: 4, bottom: 2 }
const REPORT_LINE_CHART_MARGIN_DUAL = { top: 8, right: 28, left: 4, bottom: 2 }
const REPORT_BAR_CHART_MARGIN = { top: 24, right: 12, left: 4, bottom: 0 }
const REPORT_BAR_CATEGORY_GAP = '16%'
const REPORT_YAXIS_WIDTH = 46

function ReferralReputationReport(): React.ReactElement {
  const [bucketView, setBucketView] = useState<ReportsDistributionView>('Count')
  const [reportSaveViewAction, setReportSaveViewAction] =
    useState<JSX.Element | null>(null)
  const [reportGeneratePDFAction, setReportGeneratePDFAction] =
    useState<JSX.Element | null>(null)
  // `reportFiltersAndMetrics` is the source of truth for the filters and metrics of the currently selected report view.
  // Use this for all data queries and any logic that depends on the selected report view.
  const [reportFiltersAndMetrics, setReportFiltersAndMetrics] = useState<{
    filters: Record<string, boolean>
    metrics: Record<string, boolean>
  } | null>(null)
  const feedbackChannelsFullView = useReportFullViewSection('ref-channels')

  const bucketTotalPatients = useMemo(
    () => REFERRAL_VALUE_BUCKET_ROWS.reduce((sum, r) => sum + r.count, 0) || 1,
    [],
  )

  const bucketChartData = useMemo(
    () =>
      REFERRAL_VALUE_BUCKET_ROWS.map((row) => {
        const pct = Math.round((row.count / bucketTotalPatients) * 1000) / 10
        const barValue = bucketView === 'Count' ? row.count : pct
        const topLabel = bucketView === 'Count' ? String(row.count) : `${pct}%`
        return { ...row, barValue, topLabel, pct }
      }),
    [bucketView, bucketTotalPatients],
  )

  const bucketYAxis = useMemo(() => {
    if (bucketView === 'Count') {
      return {
        domain: [0, 70] as [number, number],
        ticks: [0, 10, 20, 30, 40, 50, 60, 70],
        tickFormatter: (v: number) => String(v),
      }
    }
    return {
      domain: [0, 60] as [number, number],
      ticks: [0, 15, 30, 45, 60],
      tickFormatter: (v: number) => `${v}%`,
    }
  }, [bucketView])

  const channelColumns: ReportDataTableColumn<FeedbackChannelRow>[] = useMemo(
    () => [
      { key: 'channel', label: 'Channel' },
      { key: 'responses', label: 'Responses' },
      {
        key: 'sharePct',
        label: 'Share',
        render: (row) => `${row.sharePct}%`,
      },
      {
        key: 'avgRating',
        label: 'Avg. Rating',
        render: (row) => String(row.avgRating),
      },
    ],
    [],
  )

  return (
    <div className="reports-patient-base-health reports-treatment-conversion-funnel reports-referral-reputation">
      <ReportBundleStickyTopWhenExpanded>
        <ReportPageHeader title="Referral & Reputation">
          {reportSaveViewAction}
          {reportGeneratePDFAction}
        </ReportPageHeader>

        <ReportFilterToolbar
          filters={REFERRAL_REPUTATION_FILTERS}
          onReportSaveViewActionChange={setReportSaveViewAction}
          onReportGeneratePDFActionChange={setReportGeneratePDFAction}
          onReportFiltersAndMetricsChange={setReportFiltersAndMetrics}
        />
      </ReportBundleStickyTopWhenExpanded>

      <div className="reports-treatment-conversion-funnel__stack">
        <ReportExpandableSection sectionId="ref-upper">
          <ReportKpiGrid items={REFERRAL_REPUTATION_KPI_METRICS} />

          <div className="reports-treatment-conversion-funnel__grid">
            <ReportSectionCard className="reports-patient-base-health__chart-card">
              <div className="reports-patient-value-revenue__column">
                <div className="reports-patient-base-health__chart-head">
                  <ReportSectionHeading>
                    Patient Referrals Made (Monthly)
                  </ReportSectionHeading>
                </div>
                <p className="reports-treatment-conversion-funnel__chart-note">
                  Count of referrals captured in CRM
                </p>
                <div className="reports-treatment-conversion-funnel__chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={REFERRAL_MONTHLY_MADE_DATA}
                      margin={REPORT_LINE_CHART_MARGIN}
                    >
                      <CartesianGrid
                        vertical={false}
                        strokeDasharray="0"
                        stroke={REPORT_CHART_GRID_STROKE}
                      />
                      <XAxis
                        dataKey="month"
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                      />
                      <YAxis
                        domain={[0, 30]}
                        ticks={[0, 5, 10, 15, 20, 25, 30]}
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                        width={REPORT_YAXIS_WIDTH}
                      />
                      <Tooltip
                        formatter={(value: number) => [value, 'Referrals']}
                        contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                      />
                      <Line
                        type="linear"
                        dataKey="referrals"
                        name="Referrals"
                        stroke="var(--blue-500)"
                        strokeWidth={2}
                        dot={false}
                        activeDot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </ReportSectionCard>

            <ReportSectionCard className="reports-patient-base-health__chart-card">
              <div className="reports-patient-value-revenue__column">
                <div className="reports-patient-base-health__chart-head">
                  <ReportSectionHeading>
                    Unscheduled Treatment Opportunities (Value Buckets)
                  </ReportSectionHeading>
                  <ReportViewTabs
                    options={REPORTS_DISTRIBUTION_VIEW_TABS}
                    value={bucketView}
                    onChange={(value) =>
                      setBucketView(value as ReportsDistributionView)
                    }
                    className="reports-patient-base-health__view-group"
                  />
                </div>
                <p className="reports-treatment-conversion-funnel__chart-note">
                  Patients by unscheduled value bucket
                </p>
                <div className="reports-treatment-conversion-funnel__chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={bucketChartData}
                      margin={REPORT_BAR_CHART_MARGIN}
                      barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                    >
                      <CartesianGrid
                        vertical={false}
                        strokeDasharray="0"
                        stroke={REPORT_CHART_GRID_STROKE}
                      />
                      <XAxis
                        dataKey="label"
                        axisLine={false}
                        tickLine={false}
                        interval={0}
                        tick={REPORT_CHART_AXIS_TICK}
                      />
                      <YAxis
                        domain={bucketYAxis.domain}
                        ticks={bucketYAxis.ticks}
                        tickFormatter={bucketYAxis.tickFormatter}
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                        width={REPORT_YAXIS_WIDTH}
                      />
                      <Tooltip
                        formatter={(
                          value: number,
                          _name: string,
                          item: { payload?: { feesK?: number } },
                        ) => {
                          const fees = item.payload?.feesK
                          if (bucketView === 'Count')
                            return [
                              fees != null
                                ? `${value} patients · $${fees}K`
                                : String(value),
                              'Bucket',
                            ]
                          return [`${value}%`, 'Share']
                        }}
                        contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                      />
                      <Bar
                        dataKey="barValue"
                        radius={[8, 8, 8, 8]}
                        fill="var(--blue-400)"
                        barSize={100}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </ReportSectionCard>
          </div>
        </ReportExpandableSection>

        <div className="reports-treatment-conversion-funnel__grid">
          <ReportExpandableSection sectionId="ref-nps">
            <ReportSectionCard className="reports-patient-base-health__chart-card">
              <div className="reports-patient-value-revenue__column">
                <div className="reports-patient-base-health__chart-head">
                  <ReportSectionHeading>
                    NPS / Feedback Trend (Monthly)
                  </ReportSectionHeading>
                </div>
                <p className="reports-treatment-conversion-funnel__chart-note">
                  NPS score and survey volume over time
                </p>
                <div className="reports-treatment-conversion-funnel__chart reports-referral-reputation__nps-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={NPS_FEEDBACK_TREND_MONTHLY}
                      margin={REPORT_LINE_CHART_MARGIN_DUAL}
                    >
                      <CartesianGrid
                        vertical={false}
                        strokeDasharray="0"
                        stroke={REPORT_CHART_GRID_STROKE}
                      />
                      <XAxis
                        dataKey="month"
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                      />
                      <YAxis
                        yAxisId="nps"
                        domain={[0, 60]}
                        ticks={[0, 10, 20, 30, 40, 50, 60]}
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                        width={REPORT_YAXIS_WIDTH}
                        label={{
                          value: 'NPS',
                          angle: -90,
                          position: 'insideLeft',
                          fill: 'var(--gray-500)',
                          fontSize: 11,
                        }}
                      />
                      <YAxis
                        yAxisId="responses"
                        orientation="right"
                        domain={[0, 260]}
                        ticks={[0, 50, 100, 150, 200, 250]}
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                        width={REPORT_YAXIS_WIDTH}
                        label={{
                          value: 'Responses',
                          angle: 90,
                          position: 'insideRight',
                          fill: 'var(--gray-500)',
                          fontSize: 11,
                        }}
                      />
                      <Tooltip
                        formatter={(value: number, name: string) => {
                          if (name === 'nps') return [`${value}`, 'NPS']
                          return [value, 'Responses']
                        }}
                        contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                      />
                      <Line
                        yAxisId="nps"
                        type="linear"
                        dataKey="nps"
                        name="nps"
                        stroke="var(--blue-500)"
                        strokeWidth={2}
                        dot={false}
                        activeDot={false}
                      />
                      <Line
                        yAxisId="responses"
                        type="linear"
                        dataKey="responses"
                        name="responses"
                        stroke="var(--violet-500)"
                        strokeWidth={2}
                        dot={false}
                        activeDot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </ReportSectionCard>
          </ReportExpandableSection>

          <ReportExpandableSection sectionId="ref-channels">
            <ReportSectionCard className="reports-patient-base-health__chart-card reports-treatment-conversion-funnel__table-card">
              <div className="reports-patient-base-health__chart-head">
                <ReportFullViewTableTitle
                  sectionId="ref-channels"
                  title="Feedback Channels (Period)"
                />
              </div>
              <p className="reports-treatment-conversion-funnel__chart-note">
                Where feedback is coming from (period)
              </p>
              <ReportDataTable
                key={
                  feedbackChannelsFullView.isFullView
                    ? 'ref-channels-fv'
                    : 'ref-channels-card'
                }
                columns={channelColumns}
                rows={FEEDBACK_CHANNEL_ROWS}
                rowKey={(row) => row.id}
                initialPageSize={10}
                className={[
                  'reports-referral-reputation__channel-table',
                  feedbackChannelsFullView.isFullView
                    ? 'reports-patient-value-revenue__provider-table--full-view'
                    : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                fullViewSectionId="ref-channels"
                fullViewSearchWithFilter
              />
            </ReportSectionCard>
          </ReportExpandableSection>
        </div>
      </div>
    </div>
  )
}

export default ReferralReputationReport
